import { canAll, type AbilityCheck, type AppAbility } from '@/lib/casl';

// Admin navigation, chosen from the ability mirror (ADR-0023 §6, §10).
// Never from role names. A System Admin's rules give the platform items;
// a Clinic Admin's rules give "My clinic". The same checks guard the routes,
// so a hidden item is also a 403 page if typed in.

export type AdminNavKey = 'clinics' | 'platform' | 'idis2go' | 'integrationStatus' | 'myClinic';

export interface AdminNavItem {
  key: AdminNavKey;
  label: string;
  to: string;
}

/** Can open other tenants through an access grant (System Admin). */
export const CROSS_TENANT_CHECK: AbilityCheck = { action: 'create', subject: 'TenantAccessGrant' };

export const ROUTE_CHECKS = {
  // Every clinic is a platform-wide read. A Clinic Admin can read `Tenant`
  // too, but only their own, so the list also needs cross-tenant access.
  clinics: [{ action: 'read', subject: 'Tenant' }, CROSS_TENANT_CHECK],
  platform: [CROSS_TENANT_CHECK],
  idis2go: [{ action: 'create', subject: 'OemCustomerLink' }],
  // Platform-wide status covers every clinic. A Clinic Admin reads only
  // their own clinic's status, on its Status tab.
  integrationStatus: [
    { action: 'read', subject: 'IntegrationStatus' },
    { action: 'read', subject: 'Tenant' },
    CROSS_TENANT_CHECK,
  ],
  myClinic: [{ action: 'read', subject: 'User' }],
} as const satisfies Record<AdminNavKey, readonly AbilityCheck[]>;

export function clinicPath(tenantId: string): string {
  return `/clinics/${tenantId}`;
}

export function adminNavItems(ability: AppAbility, ownTenantId: string | null): AdminNavItem[] {
  const items: AdminNavItem[] = [];
  const crossTenant = canAll(ability, [CROSS_TENANT_CHECK]);

  if (canAll(ability, ROUTE_CHECKS.clinics)) {
    items.push({ key: 'clinics', label: 'Clinics', to: '/clinics' });
  }
  if (crossTenant && ownTenantId !== null) {
    items.push({ key: 'platform', label: 'Platform', to: clinicPath(ownTenantId) });
  }
  if (canAll(ability, ROUTE_CHECKS.idis2go)) {
    items.push({ key: 'idis2go', label: 'IDIS2GO', to: '/idis2go-customers' });
  }
  if (canAll(ability, ROUTE_CHECKS.integrationStatus)) {
    items.push({
      key: 'integrationStatus',
      label: 'Integration status',
      to: '/integration-status',
    });
  }
  // A System Admin's own tenant is the platform, shown above as "Platform".
  if (!crossTenant && ownTenantId !== null && canAll(ability, ROUTE_CHECKS.myClinic)) {
    items.push({ key: 'myClinic', label: 'My clinic', to: clinicPath(ownTenantId) });
  }
  return items;
}
